import { ehUrlExterna, LinkExterno } from '@baiocchi/ui'
import { Fragment } from 'react'

/**
 * Rótulo dos links do corpo da interação.
 *
 * Constante e não literal espalhado: é o texto que o usuário associou a "abrir o que
 * a integração mandou", e ele precisa ser o mesmo no link do imóvel, no histórico da
 * conversa e em qualquer URL que uma integração futura traga na mensagem.
 */
export const ROTULO_LINK_INTERACAO = 'Clique Aqui'

/**
 * URL dentro de texto corrido.
 *
 * `\S+` depois do protocolo, e o recorte fino fica com `PONTUACAO_FINAL` — tentar
 * expressar "URL, mas sem a pontuação da frase" numa só expressão produz um regex que
 * ninguém revisa. Sem `g` aqui: a instância com `g` é criada por chamada, porque
 * `RegExp` com `g` guarda `lastIndex` e um regex de módulo compartilhado entre
 * renderizações devolve resultado diferente na segunda passada.
 */
const URL_EM_TEXTO = /https?:\/\/\S+/i

/**
 * Pontuação que encerra frase e **não** faz parte do endereço.
 *
 * `?` e `&` ficam **fora** desta lista de propósito: são separadores de query string, e
 * remover o `?` de `https://lais.ai/history?chatid=1` entregaria um link para a página
 * errada. Parêntese e ponto finais, sim — `(veja https://x.com/a)` é frase, não URL.
 */
const PONTUACAO_FINAL = /[.,;:!)\]}'"]+$/

interface Pedaco {
  readonly texto: string
  readonly url: string | null
}

/**
 * Quebra o texto em pedaços: trecho comum e URL, alternando.
 *
 * Exportada para teste. É a parte com regra — o componente abaixo só desenha.
 */
export function partirEmLinks(texto: string): ReadonlyArray<Pedaco> {
  const pedacos: Pedaco[] = []
  let restante = texto

  for (;;) {
    const encontro = URL_EM_TEXTO.exec(restante)
    if (encontro === null || encontro.index === undefined) break

    const bruto = encontro[0]
    // A pontuação que sobrou colada no fim volta para o texto: ela pertence à frase.
    const url = bruto.replace(PONTUACAO_FINAL, '')

    // `https://` sem endereço, ou algo que a limpeza reduziu a só o protocolo: não é
    // link, e vira texto comum em vez de uma âncora que não leva a lugar nenhum.
    if (!ehUrlExterna(url)) {
      pedacos.push({ texto: restante.slice(0, encontro.index + bruto.length), url: null })
      restante = restante.slice(encontro.index + bruto.length)
      continue
    }

    if (encontro.index > 0) {
      pedacos.push({ texto: restante.slice(0, encontro.index), url: null })
    }
    pedacos.push({ texto: url, url })
    restante = restante.slice(encontro.index + url.length)
  }

  if (restante.length > 0) pedacos.push({ texto: restante, url: null })

  return pedacos
}

export interface PropriedadesTextoComLinks {
  texto: string
  className?: string
}

/**
 * Texto de interação com as URLs viradas em link amigável.
 *
 * ## O problema que resolve
 *
 * `interacoes.mensagem` é **texto puro**, montado no servidor, e a tabela é
 * append-only por trigger (RN-25): a mensagem de uma interação já gravada não pode ser
 * reescrita, por nenhum papel. Então a apresentação amigável dos endereços que a
 * integração entrega — `link` do imóvel, `conversation_history` da Lais — **tem** de
 * acontecer na renderização. Mudar o gerador de texto no servidor só alcançaria as
 * interações futuras e deixaria todo o histórico com a URL crua.
 *
 * A consequência boa disso é que a correção vale retroativamente: os leads que já
 * estavam na base passam a mostrar "Clique Aqui" sem migration e sem tocar em dado.
 *
 * ## Por que é genérico, e não específico da Lais
 *
 * Nada aqui conhece a Lais. O que o componente sabe é "mensagem de interação pode
 * conter URL, e URL na tela é link" — então a próxima integração que escrever um
 * endereço na timeline já nasce com o comportamento certo, sem uma segunda
 * implementação. É o mesmo motivo pelo qual o `ValorDoPayload` do painel de auditoria
 * detecta URL em qualquer chave, em vez de ter uma lista de chaves conhecidas.
 *
 * ## O que **não** faz
 *
 * Não inventa link. Texto sem URL sai igual ao que entrou — é o caso do
 * `conversation_summary` da Lais, que é resumo em prosa e não tem endereço associado no
 * contrato. Se a Lais um dia passar a mandar uma URL de resumo e o servidor a escrever
 * na mensagem, ela vira "Clique Aqui" aqui sem nenhuma alteração de código.
 *
 * `whitespace-pre-wrap` no elemento pai é o que preserva as quebras de linha da
 * mensagem — os pedaços de texto entram como nós de texto e mantêm os `\n`.
 */
export function TextoComLinks({ texto, className }: PropriedadesTextoComLinks) {
  const pedacos = partirEmLinks(texto)

  return (
    <span className={className}>
      {pedacos.map((pedaco, indice) => (
        <Fragment key={indice}>
          {pedaco.url === null ? (
            pedaco.texto
          ) : (
            <LinkExterno href={pedaco.url}>{ROTULO_LINK_INTERACAO}</LinkExterno>
          )}
        </Fragment>
      ))}
    </span>
  )
}
